import { ChangeDetectionStrategy, Component, input, linkedSignal } from '@angular/core';
import { IsoDate, QueryFilter, Range } from '@softline/core';
import { TranslatePipe } from '@softline/ui-core';
import { DateRangeInputComponent, DateRangeInputValue } from '@softline/ui-core/date';
import { FilterInputBase } from '../filter-input.base';
import { FilterItemConfig } from '../../../../types/filter-config';
import { DateBetweenFilter, Filter } from '../../../../types/filter';
import { PageFilterDataService } from '../../../services/data-source/page-filter-data.service';
import { createDateRangeFilter } from '../../../../functions/date-filter.function';

/** Sidebar editor for a period with an optional comparison period. */
@Component({
  selector: 'soft-date-range-reference-filter-input',
  imports: [DateRangeInputComponent, TranslatePipe],
  templateUrl: './date-range-reference-filter-input.component.html',
  styleUrl: './date-range-reference-filter-input.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangeReferenceFilterInputComponent<T extends object> extends FilterInputBase<T, 'between'> {
  override readonly item = input.required<FilterItemConfig<T>>();
  override readonly itemFilter = input.required<(Filter<T> & { operator: 'between' }) | null>();
  override readonly operator = input.required<'between'>();
  override readonly dataService = input.required<PageFilterDataService<T>>();
  override readonly queryFilter = input.required<QueryFilter<T> | null>();

  readonly range = linkedSignal<DateRangeInputValue | null>(() => toInput(this.node()?.value));
  readonly reference = linkedSignal<DateRangeInputValue | null>(() => toInput(this.node()?.reference));

  private node(): DateBetweenFilter<T> | null {
    return this.itemFilter() as DateBetweenFilter<T> | null;
  }

  getFilterValue(): Filter<T> | null {
    const range = complete(this.range());
    if (!range) return null;
    const current = this.node();
    const node: DateBetweenFilter<T> = current
      ? { ...current, value: range }
      : createDateRangeFilter<T>(this.item().field as never, range);
    const reference = complete(this.reference());
    if (reference) node.reference = reference;
    else delete node.reference;
    return node;
  }
}

function toInput(range: Range<IsoDate> | undefined): DateRangeInputValue | null {
  return range ? { from: range.from, to: range.to } : null;
}

function complete(value: DateRangeInputValue | null): Range<IsoDate> | null {
  return value?.from && value?.to ? { from: value.from as IsoDate, to: value.to as IsoDate } : null;
}
